import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { PageHeader, SaveButton, CancelButton, ViewButton, EditButton } from "@/components/admin/shared/PageHeader";
import { PennField, PennInput, PennText, PennTextarea } from "@/components/ui";
import { calculationParameterSchema, type CalculationParameterFormData } from "@/types/admin/calculationParameter.types";
import { useCalculationParameters, useUpdateCalculationParameter } from "@/hooks/useCalculationParameters";

type Param = CalculationParameterFormData & { _id: number };
type ModalMode = "edit" | "view";
type ModalState = { mode: ModalMode; item: Param } | null;

export const CalculationParametersPage = () => {
  // svc-04 `/v1/admin/calculation-parameters` — Globals (cost_model_global_defaults)
  // + per-cost-model constants (cost_model_constants), already grouped/ordered.
  const { data } = useCalculationParameters();
  const updateParam = useUpdateCalculationParameter();
  const [items, setItems] = useState<Param[]>([]);
  const [modal, setModal] = useState<ModalState>(null);

  useEffect(() => {
    if (data?.data) {
      setItems(data.data.map((p, i) => ({ ...p, _id: i + 1 })));
    }
  }, [data]);

  const groups = useMemo(() => {
    const map = new Map<string, Param[]>();
    for (const p of items) {
      const arr = map.get(p.group) ?? [];
      arr.push(p);
      map.set(p.group, arr);
    }
    return Array.from(map.entries()).map(([group, rows]) => ({ group, rows }));
  }, [items]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CalculationParameterFormData>({
    resolver: zodResolver(calculationParameterSchema),
  });

  const openEdit = (item: Param) => { reset(item); setModal({ mode: "edit", item }); };
  const openView = (item: Param) => { reset(item); setModal({ mode: "view", item }); };

  const onSubmit = (data: CalculationParameterFormData) => {
    if (!modal) return;
    // `value` + `unit` + `note` persist for both Globals
    // (cost_model_global_defaults) and per-model constants
    // (cost_model_constants) — both tables carry unit/note columns as of the
    // 2026-06-08 admin-panel-fixes migration. id/label/group/binding are
    // identity/derived. On success the list query is invalidated; the modal
    // stays open if the save fails so the change isn't lost silently.
    updateParam.mutate(
      {
        id: modal.item.id,
        body: { value: data.value, unit: data.unit, note: data.note },
      },
      { onSuccess: () => setModal(null) },
    );
  };

  const isView = modal?.mode === "view";

  return (
    <>
      <PageHeader
        title="Dataset: Calculation Parameters"
        subtitle="Record-level editor for parameters (id: id). Parameters are grouped by the cost model or pricing rule that consumes them. Global defaults are listed separately."
      />

      <div className="space-y-6">
        {groups.map(({ group, rows }) => (
          <div key={group} className="border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <PennText size="xs" font="bold" color="blue_grey1" className="uppercase">{group}</PennText>
              <PennText size="xs" color="blue_grey">{rows.length} {rows.length === 1 ? "parameter" : "parameters"}</PennText>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((param) => (
                <div key={param._id} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <PennText size="sm" color="blue_grey" >{param.label}</PennText>
                    <PennText size="xs" color="blue_grey2" className="font-mono truncate text-blue_grey4">{param.id}</PennText>
                    {param.note && <PennText size="xs" color="blue_grey2" className="truncate text-blue_grey4">{param.note}</PennText>}
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="w-28 text-right">
                      <PennText align="end" size="sm" font="semibold" color="blue_grey" className="tabular-nums">{param.value}</PennText>
                      {param.unit && <PennText size="xs" align="end" color="blue_grey2">{param.unit}</PennText>}
                    </div>
                    <div className="flex space-x-2">
                      <ViewButton onClick={() => openView(param)} />
                      <EditButton onClick={() => openEdit(param)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <AdminModal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={isView ? "View Calculation Parameters record" : "Edit Calculation Parameters record"}
        maxWidth="max-w-2xl"
        footer={
          isView
            ? <CancelButton onClick={() => setModal(null)} />
            : <><CancelButton onClick={() => setModal(null)} /><SaveButton onClick={handleSubmit(onSubmit)} /></>
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PennField label="ID" htmlFor="id" error={!isView ? errors.id?.message : undefined}>
              <PennInput id="id" disabled {...register("id")} />
            </PennField>
            <PennField label="Label" htmlFor="label" required={!isView} error={!isView ? errors.label?.message : undefined}>
              <PennInput id="label" disabled={isView} error={!isView && !!errors.label} {...register("label")} />
            </PennField>
            <PennField label="Group" htmlFor="group" required={!isView} error={!isView ? errors.group?.message : undefined}>
              <PennInput id="group" disabled={isView} error={!isView && !!errors.group} {...register("group")} />
            </PennField>
            <PennField label="Value" htmlFor="value" required={!isView} error={!isView ? errors.value?.message : undefined}>
              <PennInput id="value" disabled={isView} error={!isView && !!errors.value} {...register("value")} />
            </PennField>
            <PennField label="Unit" htmlFor="unit" error={!isView ? errors.unit?.message : undefined}>
              <PennInput id="unit" disabled={isView} {...register("unit")} />
            </PennField>
            <PennField label="Note" htmlFor="note" error={!isView ? errors.note?.message : undefined}>
              <PennInput id="note" disabled={isView} {...register("note")} />
            </PennField>
          </div>
          <PennField label="Binding" htmlFor="binding" error={!isView ? errors.binding?.message : undefined}>
            <PennTextarea id="binding" rows={4} disabled={isView} error={!isView && !!errors.binding} {...register("binding")} />
          </PennField>
        </form>
      </AdminModal>
    </>
  );
};
